import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase, TABLES } from '@/services/supabase';
import type { TablePermission, PermissionState } from '@/types/admin';

interface Profile {
  id: string;
  email: string | null;
  full_name: string | null;
  role: string | null;
  table: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  permissions: TablePermission[];
  permissionState: PermissionState;
  loading: boolean;
  isAdmin: boolean;
  isManager: boolean;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  hasPermission: (tableName: string, action: 'read' | 'write' | 'delete') => boolean;
  getAllowedFields: (tableName: string) => string[];
}

const initialState: AuthContextType = {
  user: null,
  session: null,
  profile: null,
  permissions: [],
  permissionState: {},
  loading: true,
  isAdmin: false,
  isManager: false,
  signIn: async () => ({ error: null }),
  signUp: async () => ({ error: null }),
  signOut: async () => {},
  refreshProfile: async () => {},
  hasPermission: () => false,
  getAllowedFields: () => [],
};

export const AuthContext = createContext<AuthContextType>(initialState);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [permissions, setPermissions] = useState<TablePermission[]>([]);
  const [loading, setLoading] = useState(true);

  const isAdmin = profile?.role === 'admin';
  const isManager = profile?.role === 'manager';

  // Convert permissions array to PermissionState for easier lookup
  const permissionState: PermissionState = permissions.reduce((acc, perm) => {
    acc[perm.table_name] = {
      enabled: true,
      read: perm.can_read,
      write: perm.can_write,
      delete: perm.can_delete,
      fields: perm.allowed_fields || [],
    };
    return acc;
  }, {} as PermissionState);

  const fetchProfile = useCallback(async (userId: string) => {
    try {
      // Fetch profile
      const { data: profileData, error: profileError } = await supabase
        .from(TABLES.PROFILES)
        .select('*')
        .eq('id', userId)
        .single();

      if (profileError) {
        console.error('Error fetching profile:', profileError);
        setProfile(null);
      } else {
        setProfile(profileData as Profile);
      }

      // Fetch permissions - gracefully handle if table doesn't exist
      const { data: permData, error: permError } = await supabase
        .from('table_permissions')
        .select('*')
        .eq('user_id', userId);

      // If table doesn't exist (404), treat as no permissions but don't warn
      if (permError) {
        if ('code' in permError && permError.code === 'PGRST205') {
          // Table doesn't exist - this is expected before setup
          setPermissions([]);
        } else {
          console.warn('Error fetching permissions:', permError);
          setPermissions([]);
        }
      } else {
        setPermissions((permData || []) as TablePermission[]);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfile(null);
      setPermissions([]);
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  }, [user, fetchProfile]);

  // Check if user has permission for a specific action
  const hasPermission = useCallback(
    (tableName: string, action: 'read' | 'write' | 'delete'): boolean => {
      // Admins have full access
      if (isAdmin) return true;

      const perm = permissions.find((p) => p.table_name === tableName);
      if (!perm) return false;

      switch (action) {
        case 'read':
          return perm.can_read;
        case 'write':
          return perm.can_write;
        case 'delete':
          return perm.can_delete;
        default:
          return false;
      }
    },
    [permissions, isAdmin]
  );

  // Get allowed fields for a table
  const getAllowedFields = useCallback(
    (tableName: string): string[] => {
      // Admins see all fields
      if (isAdmin) return [];

      const perm = permissions.find((p) => p.table_name === tableName);
      if (!perm) return [];

      return perm.allowed_fields || [];
    },
    [permissions, isAdmin]
  );

  useEffect(() => {
    // Get initial session with error handling
    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          fetchProfile(session.user.id);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error getting session:', error);
        setLoading(false);
      });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setProfile(null);
        setPermissions([]);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  }, []);

  const signUp = useCallback(async (email: string, password: string, fullName: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
        },
      },
    });
    return { error };
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setPermissions([]);
  }, []);

  const value = {
    user,
    session,
    profile,
    permissions,
    permissionState,
    loading,
    isAdmin,
    isManager,
    signIn,
    signUp,
    signOut,
    refreshProfile,
    hasPermission,
    getAllowedFields,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Permission check hook
export function usePermission(tableName: string) {
  const { hasPermission, getAllowedFields, isAdmin } = useAuth();

  return {
    canRead: hasPermission(tableName, 'read'),
    canWrite: hasPermission(tableName, 'write'),
    canDelete: hasPermission(tableName, 'delete'),
    allowedFields: getAllowedFields(tableName),
    isAdmin,
    isRestricted: !isAdmin,
  };
}
